import { ShiftProfileGroup, ShiftProfileGroupCreate } from 'repository/schedule/@types/ShiftProfileGroup';

interface ShiftProfileGroupApiResponse {
    status: string;
    data: ShiftProfileGroup[];
}

interface ShiftProfileGroupApiOneResponse {
    status: string;
    data: ShiftProfileGroup;
}

const normalizeGroup = (item: ShiftProfileGroup): ShiftProfileGroup => ({
    ...item,
    id: item.shift_profile_group_id,
    shift_profile_group_description: item.shift_profile_group_description ?? '',
    shift_profiles: item.shift_profiles ?? [],
});

const buildUpsertPayload = (
    data: ShiftProfileGroupCreate | ShiftProfileGroup,
    id?: number,
): ShiftProfileGroupCreate | ShiftProfileGroup => ({
    ...(id !== undefined ? { shift_profile_group_id: id } : {}),
    shift_profile_group_name: data.shift_profile_group_name,
    shift_profile_group_description: data.shift_profile_group_description ?? '',
    shift_profiles: (data.shift_profiles ?? []).map((item) => ({
        shift_profile_id: item.shift_profile_id as number,
        monday: item.monday,
        tuesday: item.tuesday,
        wednesday: item.wednesday,
        thursday: item.thursday,
        friday: item.friday,
        saturday: item.saturday,
        sunday: item.sunday,
    })),
});

export class ShiftProfileGroupRepository {
    private httpService: IHttpService;
    private baseUrl: string;


    constructor(httpService: IHttpService) {
        this.httpService = httpService;
        this.baseUrl = '/shift_profile_groups';
    }

    async getAll(): Promise<ShiftProfileGroup[]> {
        const response = await this.httpService.get<ShiftProfileGroupApiResponse>(this.baseUrl);
        return (response.data ?? [])
            .map(normalizeGroup)
            .sort((a, b) => a.shift_profile_group_name.localeCompare(b.shift_profile_group_name));
    }

    async getOne(id: number): Promise<ShiftProfileGroup> {
        const response = await this.httpService.get<ShiftProfileGroupApiOneResponse>(`${this.baseUrl}/${id}`);
        const item = response.data;

        if (!item) {
            throw new Error(`ShiftProfileGroup ${id} not found`);
        }

        return normalizeGroup(item);
    }

    async create(data: ShiftProfileGroupCreate): Promise<ShiftProfileGroup> {
        const payload = buildUpsertPayload(data);
        return await this.httpService.post<ShiftProfileGroup, ShiftProfileGroupCreate | ShiftProfileGroup>(this.baseUrl, {
            data: payload,
        });
    }

    async update(id: number, data: ShiftProfileGroup): Promise<ShiftProfileGroup> {
        const payload = buildUpsertPayload(data, id);
        return await this.httpService.patch<ShiftProfileGroup, ShiftProfileGroupCreate | ShiftProfileGroup>(
            `${this.baseUrl}/${id}`,
            { data: payload },
        );
    }

    async delete(id: number): Promise<void> {
        await this.httpService.delete<void>(`${this.baseUrl}/${id}`);
    }
}
